import { AlertCircle, CheckCircle2, Mail } from 'lucide-react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';

import AuthLayout from '../components/AuthLayout';
import { Dropdown, Input, PasswordInput, PhoneInput } from '../components/forms';
import { authService } from '../services/authService';
import { getCountryCodes, validatePhoneNumber } from '../utils/phoneValidation';
import {
  getPasswordStrength,
  validateCompanyName,
  validateEmail,
  validateName,
  validatePassword,
  validatePasswordConfirmation,
} from '../utils/validationHelpers';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  mobileNumber: string;
  countryCode: string;
  workEmail: string;
  companyName: string;
  companyType: string;
  password: string;
  confirmPassword: string;
}

const companyTypes = [
  { value: 'General Contractor', label: 'General Contractor' },
  { value: 'Subcontractor', label: 'Subcontractor' },
  { value: 'Developer', label: 'Developer' },
  { value: 'Architect', label: 'Architect' },
  { value: 'Engineer', label: 'Engineer' },
  { value: 'Property Management', label: 'Property Management' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Other', label: 'Other' },
];

const countryCodes = getCountryCodes();

const Register: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState<{
    isChecked: boolean;
    isAvailable: boolean;
    message?: string;
  }>({
    isChecked: false,
    isAvailable: false,
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<RegisterFormData>({
    defaultValues: {
      countryCode: '+1',
      companyType: '',
    },
  });

  const password = watch('password');
  // const confirmPassword = watch('confirmPassword');
  const workEmail = watch('workEmail');
  const selectedCountryCode = watch('countryCode');
  const selectedCompanyType = watch('companyType');
  const mobileNumber = watch('mobileNumber');

  // Get password strength
  const passwordStrength = getPasswordStrength(password || '');

  // Email availability checking with debounce
  const checkEmailAvailability = useCallback(
    async (email: string) => {
      if (!email || validateEmail(email).isValid !== true) {
        setEmailStatus({ isChecked: false, isAvailable: false });
        return;
      }

      setIsCheckingEmail(true);
      try {
        const response = await authService.checkEmailAvailability(email);
        if (response.success && response.data) {
          setEmailStatus({
            isChecked: true,
            isAvailable: response.data.available,
            message: response.data.available
              ? t('auth.emailAvailable')
              : t('auth.emailAlreadyRegistered'),
          });
        } else {
          // If API endpoint doesn't exist yet, assume email is available for development
          setEmailStatus({
            isChecked: true,
            isAvailable: true,
            message: t('auth.emailFormatValid'),
          });
        }
      } catch {
        // If API endpoint doesn't exist yet, assume email is available for development
        setEmailStatus({
          isChecked: true,
          isAvailable: true,
          message: t('auth.emailFormatValid'),
        });
      } finally {
        setIsCheckingEmail(false);
      }
    },
    [t]
  );

  // Debounced email checking
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (workEmail && workEmail.length > 0) {
        checkEmailAvailability(workEmail);
      } else {
        setEmailStatus({ isChecked: false, isAvailable: false });
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [workEmail, checkEmailAvailability]);

  // Re-validate phone number when country code changes
  useEffect(() => {
    if (mobileNumber && selectedCountryCode) {
      const validation = validatePhoneNumber(mobileNumber, selectedCountryCode);
      if (!validation.isValid) {
        // Clear the phone number if it's invalid for the new country
        setValue('mobileNumber', '');
      }
    }
  }, [selectedCountryCode, mobileNumber, setValue]);

  const onSubmit = async (data: RegisterFormData) => {
    // Final validation before submission
    // Only block if email is explicitly unavailable (not if API check failed)
    if (
      emailStatus.isChecked &&
      !emailStatus.isAvailable &&
      emailStatus.message?.includes('already registered')
    ) {
      setError(t('auth.errors.useDifferentEmail'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await authContext?.register(data);
      if (result?.success) {
        // Show success message
        setShowSuccess(true);

        // Store email for verification
        localStorage.setItem('registration_email', data.workEmail);

        // Navigate to verification after a brief delay to show success
        setTimeout(() => {
          navigate('/verification');
        }, 1500);
      } else {
        setError(result?.message || t('auth.anErrorOccurred'));
        setShowSuccess(false);
      }
    } catch {
      setError(t('auth.anErrorOccurred'));
      setShowSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title={t('auth.signUp')} subtitle={t('auth.signUpToManageAccount')}>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {showSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle2 className="text-green-500 mr-2" size={20} />
            <div>
              <p className="text-sm text-green-800 font-medium">
                {t('auth.registrationSuccessful')}
              </p>
              <p className="text-xs text-green-600 mt-1">
                <Mail className="inline mr-1" size={12} />
                {t('auth.checkEmailForCode')}
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Name fields */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            {...register('firstName', {
              required: t('auth.errors.firstNameRequired'),
              validate: value => {
                const validation = validateName(value, 'First name');
                return validation.isValid || validation.message;
              },
            })}
            label={t('auth.firstName')}
            placeholder={t('auth.enterFirstName')}
            error={errors.firstName?.message}
          />
          <Input
            {...register('lastName', {
              required: t('auth.errors.lastNameRequired'),
              validate: value => {
                const validation = validateName(value, 'Last name');
                return validation.isValid || validation.message;
              },
            })}
            label={t('auth.lastName')}
            placeholder={t('auth.enterLastName')}
            error={errors.lastName?.message}
          />
        </div>

        {/* Mobile Number */}
        <PhoneInput
          {...register('mobileNumber', {
            required: t('auth.errors.mobileNumberRequired'),
            validate: (value: string) => {
              const currentCountryCode = watch('countryCode') || selectedCountryCode;
              if (!value || value.length === 0) {
                return t('auth.errors.mobileNumberRequired');
              }
              const validation = validatePhoneNumber(value, currentCountryCode);
              return validation.isValid || validation.message || 'Invalid phone number';
            },
          })}
          label={t('auth.mobileNumber')}
          countryCodes={countryCodes}
          selectedCountryCode={selectedCountryCode}
          onCountryCodeChange={code => {
            setValue('countryCode', code);
            // Clear mobile number when country changes
            setValue('mobileNumber', '');
            // Trigger validation for mobile number field
            trigger('mobileNumber');
          }}
          onChange={value => {
            setValue('mobileNumber', value);
            // Trigger validation after value change
            if (value) {
              trigger('mobileNumber');
            }
          }}
          error={errors.mobileNumber?.message}
        />

        {/* Work Email */}
        <div className="relative">
          <Input
            {...register('workEmail', {
              required: t('auth.errors.workEmailRequired'),
              validate: value => {
                const validation = validateEmail(value);
                if (!validation.isValid) return validation.message;

                // Only show error if email is explicitly unavailable (already registered)
                if (
                  emailStatus.isChecked &&
                  !emailStatus.isAvailable &&
                  emailStatus.message?.includes('already registered')
                ) {
                  return emailStatus.message || 'Email is not available';
                }

                return true;
              },
            })}
            type="email"
            label={t('auth.workEmail')}
            placeholder={t('auth.enterBusinessEmail')}
            error={errors.workEmail?.message}
            className={`pr-10 ${
              emailStatus.isChecked
                ? emailStatus.isAvailable
                  ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                  : 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : ''
            }`}
          />

          {/* Email status indicator */}
          <div className="absolute right-3 top-10 flex items-center">
            {isCheckingEmail ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-brand-primary"></div>
            ) : emailStatus.isChecked ? (
              emailStatus.isAvailable ? (
                <CheckCircle2 size={16} className="text-green-500" />
              ) : (
                <AlertCircle size={16} className="text-red-500" />
              )
            ) : null}
          </div>

          {/* Email status message */}
          {emailStatus.isChecked && emailStatus.message && (
            <p
              className={`mt-1 text-xs ${
                emailStatus.isAvailable ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {emailStatus.message}
            </p>
          )}
        </div>

        {/* Company fields */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            {...register('companyName', {
              required: t('auth.errors.companyNameRequired'),
              validate: value => {
                const validation = validateCompanyName(value);
                return validation.isValid || validation.message;
              },
            })}
            label={t('auth.companyName')}
            placeholder={t('auth.enterCompanyName')}
            error={errors.companyName?.message}
          />
          <Dropdown
            label={t('auth.companyType')}
            placeholder={t('auth.selectCompanyType')}
            options={companyTypes}
            value={selectedCompanyType}
            onChange={value => setValue('companyType', value)}
            error={errors.companyType?.message}
          />
        </div>

        {/* Password fields */}
        <div>
          <PasswordInput
            {...register('password', {
              required: t('auth.errors.passwordRequired'),
              validate: value => {
                const validation = validatePassword(value);
                return validation.isValid || validation.message;
              },
            })}
            label={t('auth.password')}
            placeholder="••••••••••••"
            error={errors.password?.message}
          />

          {/* Password strength indicator */}
          {password && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">{t('auth.passwordStrength')}</span>
                <span
                  className={`font-medium ${
                    passwordStrength.color === 'red'
                      ? 'text-red-600'
                      : passwordStrength.color === 'orange'
                        ? 'text-red-500'
                        : passwordStrength.color === 'yellow'
                          ? 'text-yellow-600'
                          : passwordStrength.color === 'blue'
                            ? 'text-brand-primary'
                            : passwordStrength.color === 'green'
                              ? 'text-green-600'
                              : 'text-gray-600'
                  }`}
                >
                  {passwordStrength.label}
                </span>
              </div>
              <div className="mt-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    passwordStrength.color === 'red'
                      ? 'bg-red-500'
                      : passwordStrength.color === 'orange'
                        ? 'bg-red-400'
                        : passwordStrength.color === 'yellow'
                          ? 'bg-yellow-500'
                          : passwordStrength.color === 'blue'
                            ? 'bg-brand-primary'
                            : passwordStrength.color === 'green'
                              ? 'bg-green-500'
                              : 'bg-gray-400'
                  }`}
                  style={{ width: `${(passwordStrength.score / 7) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <PasswordInput
          {...register('confirmPassword', {
            required: t('auth.errors.confirmPasswordRequired'),
            validate: value => {
              const validation = validatePasswordConfirmation(password || '', value);
              return validation.isValid || validation.message;
            },
          })}
          label={t('auth.confirmPassword')}
          placeholder="••••••••••••"
          error={errors.confirmPassword?.message}
        />

        {/* Sign up button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white font-bold py-[14px] px-[30px] rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm leading-[1.2102272851126534em] uppercase tracking-wide mt-6"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              <span>{t('auth.creatingAccount')}</span>
            </div>
          ) : (
            t('auth.signUp').toUpperCase()
          )}
        </button>

        {/* Sign in link */}
        <div className="text-right pt-2">
          <p className="text-base font-normal text-[#1F1F1F] leading-[1.75em]">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link
              to="/login"
              className="text-brand-primary hover:text-brand-primary-hover font-medium transition-colors"
            >
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};

export default Register;
